'use client';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchProductsWithPagination } from '@/hooks/useProducts';
import { ProductBase } from '@/types/product';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import StockBadge from '@/components/common/StockBadge';

interface MobileSearchViewProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  className?: string;
  onClose?: () => void;
}

const MobileSearchView: React.FC<MobileSearchViewProps> = ({
  placeholder = '¿Qué estás buscando?',
  value: externalValue,
  onChange,
  onSearch,
  className = '',
  onClose,
}) => {
  const [internalValue, setInternalValue] = useState(externalValue || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentValue = externalValue !== undefined ? externalValue : internalValue;

  const {
    data: searchResults,
    isLoading,
    error,
  } = useSearchProductsWithPagination(
    searchTerm,
    'es',
    { page: currentPage, perPage: 5 } // Menos resultados para móvil
  );

  useEffect(() => {
    if (currentValue.trim().length >= 3) {
      setSearchTerm(currentValue.trim());
      setShowDropdown(true);
      setCurrentPage(1);
    } else {
      setSearchTerm('');
      setShowDropdown(false);
    }
  }, [currentValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        // Agregar un pequeño delay para evitar cierre accidental
        closeTimeoutRef.current = setTimeout(() => {
          setShowDropdown(false);
          setIsFocused(false);
        }, 150);
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        // Para touch events, usar un delay más largo
        closeTimeoutRef.current = setTimeout(() => {
          setShowDropdown(false);
          setIsFocused(false);
        }, 300);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleTouchStart);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleInputFocus = () => {
    // Cancelar cualquier cierre pendiente
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsFocused(true);
    if (currentValue.trim().length >= 3) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // No cerrar el dropdown inmediatamente en blur
    // Solo quitar el foco visual
    setIsFocused(false);
  };

  const handleProductClick = (product: ProductBase) => {
    setShowDropdown(false);
    setIsFocused(false);
    if (onSearch) onSearch(product.title);
  };

  const handleDropdownInteraction = () => {
    // Cancelar cualquier cierre pendiente cuando se interactúa con el dropdown
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (!searchResults?.pagination) {
      return null;
    }

    const { page, totalPages, hasNext, hasPrev } = searchResults.pagination;

    if (totalPages <= 1) {
      return null;
    }

    return (
      <div data-component-id="src/components/ui/MobileSearchView.tsx:156:6" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="156" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20gap-2%20p-3%20border-t%20border-%5B%233D1C1A%5D%2F20%20bg-%5B%23FEF5E6%5D%22%7D" className="flex items-center justify-between gap-2 p-3 border-t border-[#3D1C1A]/20 bg-[#FEF5E6]">
        <button data-component-id="src/components/ui/MobileSearchView.tsx:157:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="157" data-component-file="MobileSearchView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20px-3%20py-2%20rounded-lg%20bg-%5B%233D1C1A%5D%20text-%5B%23FEF5E6%5D%20font-medium%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20hover%3Abg-%5B%233D1C1A%5D%2F90%20transition-colors%20text-sm%22%2C%22textContent%22%3A%22Anterior%22%7D"
          onClick={() => handlePageChange(page - 1)}
          disabled={!hasPrev}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#3D1C1A] text-[#FEF5E6] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3D1C1A]/90 transition-colors text-sm"
        >
          <ChevronLeft data-component-id="src/components/ui/MobileSearchView.tsx:162:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="162" data-component-file="MobileSearchView.tsx" data-component-name="ChevronLeft" data-component-content="%7B%22elementName%22%3A%22ChevronLeft%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" />
          Anterior
        </button>

        <div data-component-id="src/components/ui/MobileSearchView.tsx:166:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="166" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1%22%7D" className="flex items-center gap-1">
          <span data-component-id="src/components/ui/MobileSearchView.tsx:167:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="167" data-component-file="MobileSearchView.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%233D1C1A%5D%2F70%20font-medium%22%2C%22textContent%22%3A%22P%C3%A1gina%20de%22%7D" className="text-sm text-[#3D1C1A]/70 font-medium">
            Página {page} de {totalPages}
          </span>
        </div>

        <button data-component-id="src/components/ui/MobileSearchView.tsx:172:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="172" data-component-file="MobileSearchView.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20px-3%20py-2%20rounded-lg%20bg-%5B%233D1C1A%5D%20text-%5B%23FEF5E6%5D%20font-medium%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20hover%3Abg-%5B%233D1C1A%5D%2F90%20transition-colors%20text-sm%22%2C%22textContent%22%3A%22Siguiente%22%7D"
          onClick={() => handlePageChange(page + 1)}
          disabled={!hasNext}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#3D1C1A] text-[#FEF5E6] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3D1C1A]/90 transition-colors text-sm"
        >
          Siguiente
          <ChevronRight data-component-id="src/components/ui/MobileSearchView.tsx:178:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="178" data-component-file="MobileSearchView.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const renderProductItem = (product: ProductBase) => {
    const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
    const originalPriceNumber =
      product?.priceOriginalInc !== undefined
        ? parseFloat(String(product?.priceOriginalInc))
        : undefined;
    const hasDiscount =
      originalPriceNumber !== undefined &&
      !isNaN(originalPriceNumber) &&
      originalPriceNumber > currentPriceNumber;
    const discountPercent = hasDiscount
      ? Math.round(((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100)
      : 0;
    return (
      <Link data-component-id="src/components/ui/MobileSearchView.tsx:198:6" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="198" data-component-file="MobileSearchView.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20p-3%20hover%3Abg-%5B%233D1C1A%5D%2F10%20transition-colors%20border-b%20border-%5B%233D1C1A%5D%2F10%20last%3Aborder-b-0%20group%22%7D"
        key={product.id}
        href={product.url || ''}
        onClick={() => handleProductClick(product)}
        className="flex items-center gap-3 p-3 hover:bg-[#3D1C1A]/10 transition-colors border-b border-[#3D1C1A]/10 last:border-b-0 group"
      >
        <div data-component-id="src/components/ui/MobileSearchView.tsx:204:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="204" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-10%20h-10%20relative%22%7D" className="flex-shrink-0 w-10 h-10 relative">
          <StockBadge data-component-id="src/components/ui/MobileSearchView.tsx:205:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="205" data-component-file="MobileSearchView.tsx" data-component-name="StockBadge" data-component-content="%7B%22elementName%22%3A%22StockBadge%22%2C%22className%22%3A%22top-4%20right-0%20left-auto%22%7D" productId={product.id} variant="compact" className="top-4 right-0 left-auto" />
          <Image data-component-id="src/components/ui/MobileSearchView.tsx:206:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="206" data-component-file="MobileSearchView.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%20rounded%22%7D"
            src={product.image || ''}
            alt={product.title || ''}
            width={40}
            height={40}
            className="w-full h-full object-contain rounded"
          />
          {hasDiscount && (
            <div data-component-id="src/components/ui/MobileSearchView.tsx:214:12" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="214" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-top-1%20-right-1%20bg-%5B%23fcc00a%5D%20text-%5B%233D1C1A%5D%20text-xs%20font-bold%20rounded-full%20w-4%20h-4%20flex%20items-center%20justify-center%22%2C%22textContent%22%3A%22-%20%25%22%7D" className="absolute -top-1 -right-1 bg-[#fcc00a] text-[#3D1C1A] text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
              -{discountPercent}%
            </div>
          )}
        </div>
        <div data-component-id="src/components/ui/MobileSearchView.tsx:219:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="219" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-grow%20min-w-0%22%7D" className="flex-grow min-w-0">
          <h3 data-component-id="src/components/ui/MobileSearchView.tsx:220:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="220" data-component-file="MobileSearchView.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-medium%20text-%5B%233D1C1A%5D%20truncate%20group-hover%3Atext-%5B%233D1C1A%5D%2F80%20transition-colors%22%7D"
            className="text-sm font-medium text-[#3D1C1A] truncate group-hover:text-[#3D1C1A]/80 transition-colors"
            style={{
              fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
              fontWeight: '700',
            }}
          >
            {product.title}
          </h3>
          <p data-component-id="src/components/ui/MobileSearchView.tsx:229:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="229" data-component-file="MobileSearchView.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F70%20truncate%22%7D"
            className="text-xs text-[#3D1C1A]/70 truncate"
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400',
            }}
          >
            {product.brand}
          </p>
          {product.productCombinations && product.productCombinations.length > 0 && (
            <p data-component-id="src/components/ui/MobileSearchView.tsx:239:12" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="239" data-component-file="MobileSearchView.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F60%20truncate%22%7D" className="text-xs text-[#3D1C1A]/60 truncate">
              {product.productCombinations
                .find((combination) => combination.isDefault)
                ?.productOptions?.map((option) => option.name)
                .join(', ')}
            </p>
          )}
        </div>
        <div data-component-id="src/components/ui/MobileSearchView.tsx:247:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="247" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20text-right%22%7D" className="flex-shrink-0 text-right">
          <div data-component-id="src/components/ui/MobileSearchView.tsx:248:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="248" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-sm%20font-bold%20text-%5B%233D1C1A%5D%22%2C%22textContent%22%3A%22%E2%82%AC%22%7D"
            className="text-sm font-bold text-[#3D1C1A]"
            style={{
              fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
              fontWeight: '700',
            }}
          >
            {product.priceFinalInc} €
          </div>
          {hasDiscount && (
            <div data-component-id="src/components/ui/MobileSearchView.tsx:258:12" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="258" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F60%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%22%7D" className="text-xs text-[#3D1C1A]/60 line-through">
              {originalPriceNumber?.toFixed(2)} €
            </div>
          )}
        </div>
      </Link>
    );
  };

  const renderDropdown = () => {
    if (!showDropdown) return null;
    return (
      <div data-component-id="src/components/ui/MobileSearchView.tsx:270:6" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="270" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20right-0%20mt-1%20bg-%5B%23FEF5E6%5D%20border%20border-%5B%233D1C1A%5D%2F20%20rounded-lg%20shadow-lg%20z-50%20max-h-80%20overflow-y-auto%20scrollbar-hide%22%7D"
        ref={dropdownRef}
        className="absolute top-full left-0 right-0 mt-1 bg-[#FEF5E6] border border-[#3D1C1A]/20 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto scrollbar-hide"
        style={{ boxShadow: '0 10px 25px rgba(61, 28, 26, 0.15)' }}
        onMouseEnter={handleDropdownInteraction}
        onTouchStart={handleDropdownInteraction}
        onScroll={handleDropdownInteraction}
      >
        {isLoading ? (
          <div data-component-id="src/components/ui/MobileSearchView.tsx:279:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="279" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20p-4%22%7D" className="flex items-center justify-center p-4">
            <div data-component-id="src/components/ui/MobileSearchView.tsx:280:12" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="280" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-6%20w-6%20border-b-2%20border-%5B%233D1C1A%5D%22%7D" className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3D1C1A]"></div>
            <span data-component-id="src/components/ui/MobileSearchView.tsx:281:12" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="281" data-component-file="MobileSearchView.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-2%20text-sm%20text-%5B%233D1C1A%5D%20font-medium%22%2C%22textContent%22%3A%22Buscando...%22%7D" className="ml-2 text-sm text-[#3D1C1A] font-medium">Buscando...</span>
          </div>
        ) : error ? (
          <div data-component-id="src/components/ui/MobileSearchView.tsx:284:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="284" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20text-center%20text-red-600%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Error%20al%20buscar%20productos%22%7D" className="p-4 text-center text-red-600 text-sm font-medium">
            Error al buscar productos
          </div>
        ) : !searchResults?.data || searchResults.data.length === 0 ? (
          <div data-component-id="src/components/ui/MobileSearchView.tsx:288:10" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="288" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20text-center%20text-%5B%233D1C1A%5D%2F60%20text-sm%20font-medium%22%2C%22textContent%22%3A%22No%20se%20encontraron%20productos%22%7D" className="p-4 text-center text-[#3D1C1A]/60 text-sm font-medium">
            No se encontraron productos
          </div>
        ) : (
          <>
            {searchResults.data.map(renderProductItem)}
            {renderPagination()}
            {searchResults.pagination && searchResults.pagination.total > 1 && (
              <div data-component-id="src/components/ui/MobileSearchView.tsx:296:14" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="296" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20text-center%20border-t%20border-%5B%233D1C1A%5D%2F20%20bg-%5B%23FEF5E6%5D%22%7D" className="p-3 text-center border-t border-[#3D1C1A]/20 bg-[#FEF5E6]">
                <span data-component-id="src/components/ui/MobileSearchView.tsx:297:16" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="297" data-component-file="MobileSearchView.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%233D1C1A%5D%2F60%20font-medium%22%2C%22textContent%22%3A%22Mostrando%20de%20%20%20resultados%22%7D" className="text-xs text-[#3D1C1A]/60 font-medium">
                  Mostrando {searchResults.data.length} de {searchResults.pagination.total}{' '}
                  resultados
                </span>

                <div data-component-id="src/components/ui/MobileSearchView.tsx:302:16" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="302" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%22%7D" className="mt-2">
                  <Link data-component-id="src/components/ui/MobileSearchView.tsx:303:18" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="303" data-component-file="MobileSearchView.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-%5B%233D1C1A%5D%20underline%20hover%3Aopacity-80%20transition%22%2C%22textContent%22%3A%22Mostrar%20todo%22%7D"
                    href={`/busqueda?q=${encodeURIComponent(currentValue.trim())}&page=1`}
                    onClick={() => {
                      setShowDropdown(false);
                      setIsFocused(false);
                      onClose?.();
                    }}
                    className="text-sm font-semibold text-[#3D1C1A] underline hover:opacity-80 transition"
                  >
                    Mostrar todo
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div data-component-id="src/components/ui/MobileSearchView.tsx:324:4" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="324" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
      <div data-component-id="src/components/ui/MobileSearchView.tsx:325:6" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="325" data-component-file="MobileSearchView.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20rounded-lg%20px-4%20py-3%22%7D"
        className="flex items-center rounded-lg px-4 py-3"
        style={{ backgroundColor: 'rgba(61, 28, 26, 0.1)' }}
      >
        <Image data-component-id="src/components/ui/MobileSearchView.tsx:329:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="329" data-component-file="MobileSearchView.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_search.svg%22%2C%22alt%22%3A%22Search%22%2C%22className%22%3A%22mr-3%22%7D" src="/images/img_search.svg" alt="Search" width={18} height={18} className="mr-3" />
        <input data-component-id="src/components/ui/MobileSearchView.tsx:330:8" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="330" data-component-file="MobileSearchView.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AcurrentValue%5D%22%2C%22className%22%3A%22flex-1%20text-sm%20outline-none%20bg-transparent%20search-placeholder%22%7D"
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="flex-1 text-sm outline-none bg-transparent search-placeholder"
          style={{ color: 'rgba(61, 28, 26, 0.5)' }}
        />
      </div>
      {renderDropdown()}
      <style data-component-id="src/components/ui/MobileSearchView.tsx:343:6" data-component-path="src/components/ui/MobileSearchView.tsx" data-component-line="343" data-component-file="MobileSearchView.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D" jsx>{`
        .search-placeholder::placeholder {
          color: rgba(61, 28, 26, 0.5) !important;
          font-family:
            'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif !important;
        }
        .search-placeholder::-webkit-input-placeholder {
          color: rgba(61, 28, 26, 0.5) !important;
          font-family:
            'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif !important;
        }
        .search-placeholder::-moz-placeholder {
          color: rgba(61, 28, 26, 0.5) !important;
          font-family:
            'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif !important;
        }
        .search-placeholder:-ms-input-placeholder {
          color: rgba(61, 28, 26, 0.5) !important;
          font-family:
            'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif !important;
        }
      `}</style>
    </div>
  );
};

export default MobileSearchView;
